import * as React from 'react';

import { Menu, MenuItem } from '@mui/material';
import Button from '@sdb/ui/atoms/Button';

import { Icon } from '@sdb/planning-ui';
import { IconContainer } from './Header/Header.styles';

type IconType = React.ComponentProps<typeof Icon>['type'];

interface MenuButtonProps {
  label: string;
  options: {
    label: string;
    onClick: () => void;
    icon?: IconType;
    disabled?: boolean;
  }[];
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

export default function MenuButton({
  label,
  options,
  size,
  disabled
}: MenuButtonProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // Per instance: a page can show several menu buttons, and a fixed id would repeat in the DOM.
  const id = React.useId();
  const buttonId = `${id}-button`;
  const menuId = `${id}-menu`;
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconContainer>
        <Button
          id={buttonId}
          color="primary"
          disabled={disabled}
          variant="outlined"
          disableElevation
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          endIcon={
            <IconContainer iconSize={size === 'large' ? 'MEDIUM' : 'SMALL'}>
              <Icon type="chevron-down" />
            </IconContainer>
          }
          size={size}
        >
          {label}
        </Button>
      </IconContainer>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          // On the list, not the Menu: Menu's own id lands on the popover root, while
          // aria-controls must name the element with role="menu".
          id: menuId,
          'aria-labelledby': buttonId
        }}
      >
        {options.map((option) => {
          return (
            <MenuItem
              key={option.label}
              disabled={option.disabled}
              onClick={() => {
                // MUI only stops a disabled item with pointer-events, so the handler would
                // still run for any programmatic click. An option is disabled because running
                // it twice is wrong, so the guard belongs here too.
                if (option.disabled) return;

                option.onClick();
                handleClose();
              }}
            >
              {option.icon && (
                <IconContainer iconSize={size === 'large' ? 'MEDIUM' : 'SMALL'}>
                  <Icon type={option.icon} />
                </IconContainer>
              )}
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
